import React, { useState } from 'react';

import { transparentize } from 'polished';
import PropTypes from 'prop-types';
import Overflow from 'react-overflow-indicator';
import styled, { css } from 'styled-components';

const alignmentProperties = {
  BOTTOM: 'bottom',
  LEFT: 'left',
  RIGHT: 'right',
  TOP: 'top',
};

const directionProperties = ['down', 'left', 'right', 'up'];

const sizeProperties = {
  WIDTH: 'width',
  HEIGHT: 'height',
};

const alignmentPropertyFromDirection = {
  down: alignmentProperties.BOTTOM,
  left: alignmentProperties.LEFT,
  right: alignmentProperties.RIGHT,
  up: alignmentProperties.TOP,
};

const sizePropertyFromDirection = {
  down: sizeProperties.HEIGHT,
  left: sizeProperties.WIDTH,
  right: sizeProperties.WIDTH,
  up: sizeProperties.HEIGHT,
};

const perpendicularAlignmentProperties = {
  down: css`
    left: 0;
    right: 0;
  `,
  left: css`
    top: 0;
    bottom: 0;
  `,
  right: css`
    top: 0;
    bottom: 0;
  `,
  up: css`
    left: 0;
    right: 0;
  `,
};

const StyledOverflow = styled(Overflow)`
  position: relative;
  overflow: auto;
  ${({ $showStableScrollbar }) =>
    $showStableScrollbar &&
    `
    & > [data-overflow-viewport] {
      // https://css-tricks.com/almanac/properties/s/scrollbar-gutter/
      scrollbar-gutter: stable;
    }
  `}
`;

const StyledOverflowContent = styled(Overflow.Content)`
  // Override of inline style 'display: inline-block', which
  // leaves a small space at bottom of content
  display: flex !important;
`;

const CanScroll = styled.div`
  pointer-events: none;
  user-select: none;
  position: absolute;
  opacity: 0;
  transition: opacity 0.16s ease-out;
  ${({ $color, $direction, $size, theme }) => `
    // If vertical overflow, set left and right to 0;
    // if horizontal, set top and bottom to 0.
    ${perpendicularAlignmentProperties[$direction]}
    // If vertical overflow, set height; if horizontal, set width.
    ${sizePropertyFromDirection[$direction]}: ${$size ?? theme.sizes.sm}px;
    // Set indicator position relative to direction of overflow
    ${alignmentPropertyFromDirection[$direction]}: 0;
    // Set gradient direction relative to direction of overflow
    background-image: linear-gradient(
      to ${alignmentPropertyFromDirection[$direction]},
      ${transparentize(1.0, $color ?? theme.colors.canScrollGradient)},
      ${transparentize(0.6, $color ?? theme.colors.canScrollGradient)}
    );
  `}
  ${({ $show }) =>
    $show &&
    `
      opacity: 1;
    `}
  ${({ $indicatorStyles }) => $indicatorStyles && $indicatorStyles}
`;

export default function OverflowIndicator({
  children,
  color,
  indicatorStyles,
  showStableScrollbar = false,
  size,
  ...rest
}) {
  const [canScroll, setCanScroll] = useState({
    down: false,
    left: false,
    right: false,
    up: false,
  });

  return (
    <StyledOverflow
      onStateChange={(state) => setCanScroll(state.canScroll)}
      tolerance={1}
      $showStableScrollbar={showStableScrollbar}
      {...rest}
    >
      <StyledOverflowContent $scrollState={canScroll}>
        {children}
      </StyledOverflowContent>
      {directionProperties.map((direction) => (
        <CanScroll
          key={direction}
          $color={color}
          $direction={direction}
          $indicatorStyles={indicatorStyles}
          $show={canScroll[direction]}
          $size={size}
        />
      ))}
    </StyledOverflow>
  );
}

OverflowIndicator.propTypes = {
  children: PropTypes.node.isRequired,
  /* Pass in a string color value to override the overflow indicator's gradient color */
  color: PropTypes.string,
  /* If you'd prefer to pass styles to the component, rather than styling the exported Indicator, pass styles via this prop */
  indicatorStyles: PropTypes.string,
  /* Defaults to false. To prevent content width from jumping when content is scrollable, set to true. */
  showStableScrollbar: PropTypes.bool,
  /* Pass a number to override indicators' default width or height */
  size: PropTypes.number,
};

OverflowIndicator.Wrapper = StyledOverflow;
OverflowIndicator.Content = StyledOverflowContent;
OverflowIndicator.Indicator = CanScroll;
